/**
 * Accept Invite Page - Einladung annehmen
 * 
 * Benutzer werden hier hingeleitet, wenn sie eine Einladungs-E-Mail erhalten.
 * Sie können ihr Passwort festlegen und dem Team beitreten.
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { 
  CheckCircle, XCircle, Eye, EyeOff, Loader2, 
  Users, Building2, Shield, UserCheck
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const ROLE_INFO = {
  admin: { label: 'Administrator', icon: Shield, color: 'text-purple-400', bg: 'bg-purple-500/20' },
  manager: { label: 'Manager', icon: Users, color: 'text-blue-400', bg: 'bg-blue-500/20' },
  cashier: { label: 'Kassierer', icon: UserCheck, color: 'text-green-400', bg: 'bg-green-500/20' },
  driver: { label: 'Fahrer', icon: Building2, color: 'text-orange-400', bg: 'bg-orange-500/20' },
  kitchen: { label: 'Küche', icon: Building2, color: 'text-yellow-400', bg: 'bg-yellow-500/20' }
};

export default function AcceptInvite() {
  const { token } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [inviteData, setInviteData] = useState(null);
  const [error, setError] = useState(null);
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  
  // Validiere Einladungs-Token
  useEffect(() => {
    const validateToken = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/team/validate-invite/${token}`);
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.detail || 'Einladung ungültig oder abgelaufen');
        }
        
        const data = await response.json();
        setInviteData(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    
    if (token) {
      validateToken();
    }
  }, [token]);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password.length < 8) {
      toast.error('Passwort muss mindestens 8 Zeichen lang sein');
      return;
    }
    
    if (password !== confirmPassword) {
      toast.error('Passwörter stimmen nicht überein');
      return;
    }
    
    setSubmitting(true);
    
    try {
      const response = await fetch(`${API_BASE}/api/team/accept-invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token: token,
          password: password
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Fehler beim Annehmen der Einladung');
      }
      
      const data = await response.json();
      
      toast.success('🎉 Willkommen im Team! Sie können sich jetzt anmelden.');
      
      // Speichere Token wenn vorhanden und leite weiter
      if (data.token) {
        localStorage.setItem('token', data.token);
        navigate('/app');
      } else {
        navigate('/login');
      }
      
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-500 mx-auto mb-4" />
          <p className="text-gray-400">Einladung wird überprüft...</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
        <Card className="max-w-md w-full bg-gray-800 border-red-500/50">
          <CardContent className="pt-8 pb-8 text-center">
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">Einladung ungültig</h2>
            <p className="text-gray-400 mb-6">{error}</p>
            <Button onClick={() => navigate('/login')} variant="outline">
              Zur Anmeldung
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  const roleInfo = ROLE_INFO[inviteData?.role] || ROLE_INFO.cashier;
  const RoleIcon = roleInfo.icon;
  
  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <img 
            src="/cash-x-logo.png" 
            alt="Cash-X" 
            className="h-14 mx-auto mb-4 object-contain"
            onError={(e) => { e.target.style.display = 'none' }}
          />
          <h1 className="text-2xl font-bold text-white">Willkommen bei Cash-X!</h1>
          <p className="text-gray-400">Sie wurden eingeladen, dem Team beizutreten</p>
        </div>
        
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-white">Einladung annehmen</CardTitle>
                <CardDescription>Legen Sie Ihr Passwort fest</CardDescription>
              </div>
              <Badge className={`${roleInfo.bg} ${roleInfo.color}`}>
                <RoleIcon className="w-4 h-4 mr-1" />
                {roleInfo.label}
              </Badge>
            </div>
          </CardHeader>
          
          <CardContent className="space-y-6">
            {/* Invite Info */}
            <div className="bg-gray-900/50 rounded-lg p-4 space-y-2">
              <div className="flex items-center gap-2 text-gray-400">
                <Users className="w-4 h-4" />
                <span className="text-sm">Unternehmen:</span>
                <span className="text-white font-medium">{inviteData?.company_name || 'Cash-X'}</span>
              </div>
              <div className="flex items-center gap-2 text-gray-400">
                <Building2 className="w-4 h-4" />
                <span className="text-sm">E-Mail:</span>
                <span className="text-white font-medium">{inviteData?.email}</span>
              </div>
            </div>
            
            {/* Password Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-gray-300">Neues Passwort</Label>
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Mindestens 8 Zeichen"
                    className="bg-gray-900 border-gray-600 pr-10"
                    required
                    minLength={8}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-300"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label className="text-gray-300">Passwort bestätigen</Label>
                <Input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Passwort wiederholen"
                  className="bg-gray-900 border-gray-600"
                  required
                />
                {confirmPassword && password !== confirmPassword && (
                  <p className="text-red-400 text-xs">Passwörter stimmen nicht überein</p>
                )}
              </div>
              
              <Button 
                type="submit" 
                className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                disabled={submitting || password.length < 8 || password !== confirmPassword}
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Wird verarbeitet...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Einladung annehmen
                  </>
                )}
              </Button>
            </form>
            
            <p className="text-center text-gray-500 text-xs">
              Mit dem Annehmen stimmen Sie den Nutzungsbedingungen zu.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
